import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { conversationService } from '../services/conversationService';
import './Navbar.css';

function getThemeCookie() {
  for (const c of document.cookie.split(';')) {
    const t = c.trim();
    if (t.startsWith('ml_theme=')) return t.substring(9);
  }
  return 'default';
}

function setThemeCookie(value) {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `ml_theme=${value}; expires=${expires.toUTCString()}; path=/`;
}

function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [currentTheme] = useState(() => getThemeCookie());
  const [themeToggleIdx, setThemeToggleIdx] = useState(0);

  const isAdmin = isAuthenticated && user?.roles?.includes('ADMIN');

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Poll unread count every 30s (skip for admin-only users)
  useEffect(() => {
    if (!isAuthenticated || isAdmin) return;
    const fetch = () => {
      conversationService.getUnreadCount()
        .then((res) => setUnread(res.data?.count || 0))
        .catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Build the two non-active theme options for rotation
  const allThemes = [
    { key: 'default', label: 'el original', emoji: '🌙' },
    { key: 'ml', label: 'el amarillo', emoji: '💛' },
    { key: 'fb', label: 'el azul', emoji: '💙' },
  ];
  const otherThemes = allThemes.filter((t) => t.key !== currentTheme);

  // On desktop: main button applies the shown option, cycle button rotates the option
  // On mobile: single tap cycles through all themes in order
  const themeOrder = ['default', 'ml', 'fb'];
  const nextThemeInOrder = themeOrder[(themeOrder.indexOf(currentTheme) + 1) % themeOrder.length];

  const handleThemeToggle = () => {
    // Check if mobile (cycle button hidden) — apply next in order
    if (window.innerWidth <= 600) {
      setThemeCookie(nextThemeInOrder);
      window.location.reload();
      return;
    }
    const target = otherThemes[themeToggleIdx % otherThemes.length];
    setThemeCookie(target.key);
    window.location.reload();
  };

  const handleThemeCycle = (e) => {
    e.stopPropagation();
    setThemeToggleIdx((prev) => (prev + 1) % otherThemes.length);
  };

  const toggleTarget = otherThemes[themeToggleIdx % otherThemes.length];

  const userRoleLabel = isAdmin
    ? 'Administrador'
    : user?.userType === 'PROFESSIONAL'
      ? (user.profession || 'Profesional')
      : 'Comprador / Vendedor';

  return (
    <header className="header">
      <div className="header-inner">
        <Link to="/" className="header-brand">
          <div className="header-logo">🛒</div>
          <span className="header-brand-name">MercadoLibertario</span>
        </Link>

        <div className="theme-toggle-group">
          <button className="theme-toggle-btn" onClick={handleThemeToggle}>
            <span className="theme-toggle-emoji">{toggleTarget.emoji}</span>
            <span className="theme-toggle-text">¿Preferís {toggleTarget.label}?</span>
          </button>
          <button className="theme-cycle-btn" onClick={handleThemeCycle} aria-label="Cambiar opción">
            ↻
          </button>
        </div>

        <div className="header-right">
          {isAuthenticated && (
            <span className="header-user">{user.username}</span>
          )}
          {isAuthenticated && unread > 0 && (
            <Link to="/messages" style={{ textDecoration: 'none' }}>
              <span className="unread-badge" style={{ marginRight: '0.5rem' }}>{unread}</span>
            </Link>
          )}
          <button
            className={`hamburger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </div>

      {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)} />}

      <nav className={`nav-sidebar${menuOpen ? ' open' : ''}`}>
        <div className="nav-sidebar-header">
          <span className="nav-sidebar-title">Menú</span>
          <button className="nav-close" onClick={() => setMenuOpen(false)}>×</button>
        </div>

        {isAuthenticated && (
          <>
            <div className="nav-user-info">
              <div className="nav-user-avatar">{isAdmin ? '🛡️' : '👤'}</div>
              <div>
                <div className="nav-user-name">{user.username}</div>
                <div className="nav-user-role">{userRoleLabel}</div>
              </div>
            </div>
            <div className="nav-separator" />
          </>
        )}

        <Link to="/" className="nav-link">Inicio</Link>
        {!isAdmin && (
          <Link to="/agent" className="nav-link" style={{ color: '#a78bfa', fontWeight: 600 }}>
            🤖 Asistente IA
          </Link>
        )}

        {isAuthenticated && isAdmin ? (
          /* ── Admin menu ────────────────────────────────── */
          <>
            <div style={{ padding: '0.4rem 1rem 0.2rem', fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Administración
            </div>
            <Link to="/admin" className="nav-link">📊 Dashboard</Link>
            <Link to="/admin/products" className="nav-link">📦 Publicaciones</Link>
            <Link to="/admin/users"    className="nav-link">👥 Usuarios</Link>
            <Link to="/admin/banners"  className="nav-link">🖼️ Banners</Link>
            <Link to="/admin/logs"     className="nav-link">📋 Logs del sistema</Link>
            <Link to="/admin/security" className="nav-link">🛡️ Seguridad (IPs)</Link>
            <Link to="/admin/crm" className="nav-link">📈 CRM Dashboard</Link>
            <Link to="/admin/prospects" className="nav-link">📋 CRM Prospectos</Link>
            <div className="nav-separator" />
            <Link to="/my-profile" className="nav-link">Mi perfil</Link>
            <div className="nav-separator" />
            <button
              className="nav-link nav-link-logout"
              onClick={handleLogout}
              style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
            >
              Salir
            </button>
          </>
        ) : isAuthenticated ? (
          /* ── Regular user menu ─────────────────────────── */
          <>
            <Link to="/dashboard" className="nav-link nav-link-primary">
              📊 Mi Dashboard
            </Link>
            <Link to="/new-listing" className="nav-link nav-link-primary">
              + Nueva publicación
            </Link>
            <Link to="/my-products" className="nav-link">Mis publicaciones</Link>
            <Link to="/stock" className="nav-link">Stock</Link>
            <Link to="/my-interests" className="nav-link">Interesados</Link>
            <Link to="/wishlist" className="nav-link">Lista de deseos</Link>
            <Link to="/my-purchases" className="nav-link">Mis compras</Link>
            <Link to="/messages" className="nav-link">
              Mensajes {unread > 0 && <span className="unread-badge">{unread}</span>}
            </Link>
            <Link to="/my-reviews" className="nav-link">Reseñas</Link>
            <Link to="/my-profile" className="nav-link">Mi perfil</Link>
            <div className="nav-separator" />
            <button
              className="nav-link nav-link-logout"
              onClick={handleLogout}
              style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
            >
              Salir
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className="nav-link">Ingresar</Link>
            <Link to="/register" className="nav-link nav-link-primary">Registrarse</Link>
          </>
        )}
      </nav>
    </header>
  );
}

export default Navbar;
